import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import RefreshList from '@/components/RefreshList'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { toFixedNoRound } from '@/utils'
import { useRemittanceListStatus } from '@/hooks/listStatus'

export default React.memo(function RemittanceRecord() {
  const { t } = useTranslation()
  const { getRemittanceListStatus } = useRemittanceListStatus()
  const [data, setData] = useState<ModelsTransfer[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 通用的数据加载函数
  const fetchRemittanceRecord = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const result = await APIWeb.getRemittanceRecord({
        page,
        page_size: pageSize
      })

      if (result?.list) {
        const newData = result.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载和刷新）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        if (isAppend) {
          setHasMore(false)
        } else {
          setData([])
          setHasMore(false)
        }
      }
    } catch (error) {
      console.error('获取汇款记录失败:', error)
      setHasMore(false) // 发生错误时停止加载更多
    } finally {
      setLoading(false)
    }
  }, [loading])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchRemittanceRecord(currentPage, true)
  }, [fetchRemittanceRecord, currentPage])

  // 下拉刷新处理函数
  const onRefresh = useCallback(async () => {
    await fetchRemittanceRecord(1, false)
  }, [fetchRemittanceRecord])

  // 获取状态样式
  const getStatusStyle = useCallback((status?: string) => {
    switch (status) {
      case 'success':
        return 'text-[#52C41A]'
      case 'pending':
        return 'text-[#FAAD14]'
      case 'failed':
        return 'text-[#FF4D4F]'
      default:
        return 'text-[#979797]'
    }
  }, [])

  // 渲染单个汇款记录
  const renderItem = useCallback((item: ModelsTransfer) => {
    return (
      <div className="px-6 py-4 border-b border-[#F5F5F5]">
        <div className="flex items-center justify-between mb-2">
          {/* 银行名称 */}
          <div className="flex items-center">
            <span className="text-base font-medium text-black">
              {item.bank_name || '-'}
            </span>
          </div>
          {/* 时间 */}
          <span className="text-3 text-[#979797]">
            {item.created_at_string || ''}
          </span>
        </div>

        <div className="flex items-center justify-between mb-2">
          {/* 账户名称 */}
          <div className="text-3.5 text-[#979797]">
            {t('账户名')}: {item.account_name || '-'}
          </div>
          {/* 状态 */}
          <span className={`text-3.5 font-medium ${getStatusStyle(item.status)}`}>
            {getRemittanceListStatus(item.status || '')}
          </span>
        </div>

        

        <div className="flex items-center justify-between">
          {/* 银行账户 */}
          <div className="text-3.5 text-[#979797]">
            {t('银行账户')}: {item.bank_account || '-'}
          </div>
          {/* 转账金额 */}
          <div className="text-base font-semibold text-black">
            {toFixedNoRound(item.amount || 0, 2)} {item.type.toUpperCase()}
          </div>
        </div>

        {/* 银行国家 */}
        {item.bank_country && (
          <div className="mt-2 text-3 text-[#979797]">
            {t('银行所在国家或地区')}: {item.bank_country}
          </div>
        )}

        {/* 备注 */}
        {item.remark && (
          <div className="mt-2 text-3 text-[#979797]">
            {t('备注')}: {item.remark}
          </div>
        )}
      </div>
    )
  }, [t, getStatusStyle])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('汇款记录')}</span>}
      />

      <div className="bg-white flex flex-col flex-1 ">
        <RefreshList
          data={data}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          loading={loading}
          hasMore={hasMore}
          className='h-full'
        />
      </div>
    </PageLayout>
  )
})
